import { useCallback, useEffect, useState, type ReactNode } from "react";
import { FileDownloadButton } from "../components/FileDownloadButton";
import { type Developer, devApi } from "./api";
import { DocsPage } from "./DocsPage";
import { KeysPage } from "./KeysPage";
import { formatKathmanduWhen } from "../lib/nepal-time";
import { usePageRefresh } from "../lib/page-lifecycle";

const SUBNAV: Array<{ to: string; label: string; match?: (p: string) => boolean }> = [
  { to: "/developer", label: "Overview", match: (p) => p === "/developer" || p === "/developer/overview" },
  { to: "/developer/api-keys", label: "API Keys" },
  { to: "/developer/web-sdk", label: "Web SDK" },
  { to: "/developer/flutter-sdk", label: "Flutter SDK" },
  { to: "/developer/docs", label: "API Documentation" },
  { to: "/developer/examples", label: "Integration Examples" },
];

export default function DeveloperPortal({
  path,
  go,
}: {
  me: Developer;
  path: string;
  go: (to: string) => void;
}) {
  useEffect(() => {
    document.title = "Developer — Dashcam Platform";
  }, []);

  let page: ReactNode;
  if (path === "/developer/api-keys") page = <KeysPage />;
  else if (path === "/developer/web-sdk" || path === "/developer/sdk") page = <WebSdkPage />;
  else if (path === "/developer/flutter-sdk") page = <FlutterSdkPage />;
  else if (path === "/developer/docs") page = <DocsPage />;
  else if (path === "/developer/examples") page = <ExamplesPage />;
  else page = <OverviewPage go={go} />;

  return (
    <div className="developer-layout">
      <nav className="developer-subnav" aria-label="Developer">
        {SUBNAV.map((item) => {
          const active = item.match ? item.match(path) : path === item.to;
          return (
            <button key={item.to} type="button" className={active ? "primary" : ""} onClick={() => go(item.to)}>
              {item.label}
            </button>
          );
        })}
      </nav>
      <div className="devportal-main">{page}</div>
    </div>
  );
}

function OverviewPage({ go }: { go: (to: string) => void }) {
  const [data, setData] = useState<{
    platform: { status: string; streamMode: string; api: string };
    sdk: { status: string; web: boolean; flutter: boolean; webPackage: string; flutterPackage: string; version: string };
    cameras: number;
    keys: { total: number; active: number; disabled: number; revoked: number; last_used_at: string | null };
    integration: { rest: string; gps: string; talk: string };
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await devApi<NonNullable<typeof data>>("/api/developers/overview");
      setData(next);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load overview");
    }
  }, []);

  usePageRefresh(load);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="devportal-grid">
      <section className="devportal-card">
        <h2>Developer overview</h2>
        <p>API keys, SDKs, and documentation share this Master session. There is no second login.</p>
        {error && <p className="form-error">{error}</p>}
        {data && (
          <ul className="stat-list">
            <li>
              API platform: <strong>{data.platform.status}</strong> ({data.platform.api}, {data.platform.streamMode})
            </li>
            <li>
              SDK: <strong>{data.sdk.status}</strong> · {data.sdk.webPackage} / {data.sdk.flutterPackage} {data.sdk.version}
            </li>
            <li>
              API keys: <strong>{data.keys.active}</strong> active · {data.keys.disabled} disabled · {data.keys.revoked} revoked
            </li>
            <li>
              Cameras: <strong>{data.cameras}</strong>
            </li>
            <li>Last API key use: {data.keys.last_used_at ? formatKathmanduWhen(data.keys.last_used_at) : "never"}</li>
          </ul>
        )}
        <div className="form-actions">
          <button type="button" className="primary" onClick={() => go("/developer/api-keys")}>
            API Keys
          </button>
          <button type="button" onClick={() => go("/developer/web-sdk")}>
            Web SDK
          </button>
          <button type="button" onClick={() => go("/developer/flutter-sdk")}>
            Flutter SDK
          </button>
          <button type="button" onClick={() => go("/developer/docs")}>
            Documentation
          </button>
        </div>
      </section>
      <section className="devportal-card">
        <h2>Integration</h2>
        {data && (
          <>
            <p>
              REST: <code>{data.integration.rest}</code>
            </p>
            <p>
              Live GPS: <code>{data.integration.gps}</code>
            </p>
            <p>
              PTT: <code>{data.integration.talk}</code>
            </p>
          </>
        )}
        <p className="devportal-hint">Third-party apps never receive camera IPs or terminal passwords. They call this platform with an API key.</p>
      </section>
    </div>
  );
}

function WebSdkPage() {
  return (
    <div className="devportal-grid">
      <section className="devportal-card">
        <h2>Web SDK</h2>
        <p>
          Package: <code>@dashcam/camera-sdk</code> · Version 1.0.0
        </p>
        <p>Talks to the real Partner API and the same CH1/CH2 HLS + JT1078 PTT path as this panel. Optional peer: <code>hls.js</code>.</p>
        <pre className="docs-pre">{`npm install ./dashcam-camera-sdk-1.0.0.zip
npm install hls.js`}</pre>
        <div className="form-actions">
          <FileDownloadButton url="/api/developers/docs/sdk/web.zip" filename="dashcam-camera-sdk-1.0.0.zip" label="Download Web SDK" className="primary header-action" />
        </div>
      </section>
      <section className="devportal-card">
        <h2>Authentication</h2>
        <pre className="docs-pre">{`import { CameraSDK } from '@dashcam/camera-sdk';

const sdk = new CameraSDK({
  baseUrl: window.location.origin, // or https://dashcam.mylunago.com
  apiKey: process.env.DASHCAM_API_KEY, // never commit a production key
});`}</pre>
      </section>
      <section className="devportal-card">
        <h2>Camera list</h2>
        <pre className="docs-pre">{`const { cameras } = await sdk.cameras.list();
const camera = await sdk.cameras.get(cameras[0].id);`}</pre>
      </section>
      <section className="devportal-card">
        <h2>Live camera — CH1 / CH2</h2>
        <pre className="docs-pre">{`await sdk.stream.start(camera.id, 1); // CH1 front
await sdk.stream.start(camera.id, 2); // CH2 rear
const info = await sdk.stream.get(camera.id);
const ch1 = sdk.stream.channel(info, 1);
const ch2 = sdk.stream.channel(info, 2);
await sdk.stream.stop(camera.id, 1);`}</pre>
      </section>
      <section className="devportal-card">
        <h2>Audio listen</h2>
        <pre className="docs-pre">{`const stopListen = await sdk.audio.listen(camera.id, 1, videoElement);
// unmute is handled by the SDK; audio is muxed into HLS
stopListen();`}</pre>
      </section>
      <section className="devportal-card">
        <h2>PTT / two-way audio</h2>
        <pre className="docs-pre">{`const talk = await sdk.audio.talk(camera.id, 1);
// microphone → G.711 A-law → /ws/v1/talk (same as this panel)
talk.stop();`}</pre>
      </section>
    </div>
  );
}

function FlutterSdkPage() {
  return (
    <div className="devportal-grid">
      <section className="devportal-card">
        <h2>Flutter SDK</h2>
        <p>
          Package: <code>dashcam_camera_sdk</code> · Version 1.0.0
        </p>
        <p>
          Same backend as this panel: Partner REST, HLS CH1/CH2, listen audio, JT1078 PTT. Host app should add <code>video_player</code> to play HLS URLs.
        </p>
        <pre className="docs-pre">{`# pubspec.yaml
dependencies:
  dashcam_camera_sdk:
    path: ./dashcam_camera_sdk
  video_player: ^2.9.2

# flutter run --dart-define=DASHCAM_API_KEY=dc_live_...`}</pre>
        <div className="form-actions">
          <FileDownloadButton
            url="/api/developers/docs/sdk/flutter.zip"
            filename="dashcam-flutter-sdk-1.0.0.zip"
            label="Download Flutter SDK"
            className="primary header-action"
          />
        </div>
      </section>
      <section className="devportal-card">
        <h2>Authentication</h2>
        <pre className="docs-pre">{`final sdk = CameraSDK(
  baseUrl: 'https://dashcam.mylunago.com',
  apiKey: const String.fromEnvironment('DASHCAM_API_KEY'),
);`}</pre>
      </section>
      <section className="devportal-card">
        <h2>Camera list</h2>
        <pre className="docs-pre">{`final cameras = await sdk.cameras.list();
final camera = await sdk.cameras.get(cameras.first.id);`}</pre>
      </section>
      <section className="devportal-card">
        <h2>Live camera — CH1 / CH2</h2>
        <pre className="docs-pre">{`await sdk.stream.start(cameraId: camera.id, channel: 1);
await sdk.stream.start(cameraId: camera.id, channel: 2);
final live = await sdk.stream.get(cameraId: camera.id);
final ch1 = live.channel(1);
final ch2 = live.channel(2);
// VideoPlayerController.networkUrl(Uri.parse(ch1!.hlsUrl))
await sdk.stream.stop(cameraId: camera.id, channel: 1);`}</pre>
      </section>
      <section className="devportal-card">
        <h2>Audio listen</h2>
        <pre className="docs-pre">{`final ch1 = await sdk.audio.listen(cameraId: camera.id, channel: 1);
await player.setVolume(1); // muxed into the same HLS URL`}</pre>
      </section>
      <section className="devportal-card">
        <h2>PTT / two-way audio</h2>
        <pre className="docs-pre">{`final talk = await sdk.audio.talk(cameraId: camera.id, channel: 1);
talk.sendAlaw(encodeAlawFrame(pcm8kFrame160));
await talk.stop();`}</pre>
      </section>
    </div>
  );
}

function ExamplesPage() {
  return (
    <div className="devportal-grid">
      <section className="devportal-card">
        <h2>cURL</h2>
        <pre className="docs-pre">{`export DASHCAM_API_KEY=dc_live_...
curl -sS https://dashcam.mylunago.com/api/v1/devices \\
  -H "X-Api-Key: $DASHCAM_API_KEY"

curl -sS https://dashcam.mylunago.com/api/v1/devices/CAMERA_ID/stream \\
  -H "X-Api-Key: $DASHCAM_API_KEY"

curl -sS -X POST https://dashcam.mylunago.com/api/v1/devices/CAMERA_ID/stream/start \\
  -H "X-Api-Key: $DASHCAM_API_KEY" -H "Content-Type: application/json" \\
  -d '{"channel":1}'

curl -sS -X POST https://dashcam.mylunago.com/api/v1/devices/CAMERA_ID/stream/start \\
  -H "X-Api-Key: $DASHCAM_API_KEY" -H "Content-Type: application/json" \\
  -d '{"channel":2}'`}</pre>
      </section>
      <section className="devportal-card">
        <h2>Web app</h2>
        <pre className="docs-pre">{`const sdk = new CameraSDK({ baseUrl, apiKey });
await sdk.audio.listen(id, 1, ch1Video);
await sdk.audio.listen(id, 2, ch2Video);
const talk = await sdk.audio.talk(id, 1);`}</pre>
      </section>
      <section className="devportal-card">
        <h2>Flutter app</h2>
        <pre className="docs-pre">{`final sdk = CameraSDK(baseUrl: baseUrl, apiKey: apiKey);
await sdk.stream.start(cameraId: id, channel: 1);
final ch1 = await sdk.audio.listen(cameraId: id, channel: 1);
final talk = await sdk.audio.talk(cameraId: id, channel: 1);`}</pre>
      </section>
      <section className="devportal-card">
        <h2>Errors</h2>
        <p>
          Handle <code>401</code> invalid/revoked/disabled keys, <code>403</code> missing scope, and <code>429</code> rate limits. Disabled keys stop working immediately. Camera passwords are never returned.
        </p>
      </section>
    </div>
  );
}
